import { useCallback, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import type { UserSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { EmptyRow } from '../../components/EmptyRow.tsx';
import { LoadErrorAlert } from '../../components/LoadErrorAlert.tsx';
import { LoadingRow } from '../../components/LoadingRow.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { useLoadState } from '../../hooks/useLoadState.ts';
import { UserFormDialog } from './UserFormDialog.tsx';

export function UsersPage(): React.JSX.Element {
  const { api, state: authState } = useAuth();

  const fetchUsers = useCallback(async () => (await api.users.list()).users, [api]);
  const { data: users, state, reload } = useLoadState(fetchUsers, [] as UserSummary[]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserSummary | null>(null);
  const [statusTarget, setStatusTarget] = useState<UserSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserSummary | null>(null);

  const statusAction = statusTarget?.is_active ? 'Deactivate' : 'Reactivate';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Users</Typography>
        <Button
          onClick={() => {
            setEditTarget(null);
            setDialogOpen(true);
          }}
        >
          Add user
        </Button>
      </Box>

      {state.kind === 'loading' && <LoadingRow label="Loading users..." />}

      {state.kind === 'error' && <LoadErrorAlert message={state.message} />}

      {state.kind === 'ready' && (
        <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Display name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && <EmptyRow colSpan={5} message="No users yet." />}
              {users.map((u) => {
                const isSelf = u.id === authState.userId;
                const selfActiveGuard = isSelf && u.is_active;
                return (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.display_name ?? '-'}</TableCell>
                    <TableCell>{u.role?.name ?? '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.is_active ? 'Active' : 'Inactive'}
                        color={u.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <QuietButton
                          size="small"
                          onClick={() => {
                            setEditTarget(u);
                            setDialogOpen(true);
                          }}
                        >
                          Edit
                        </QuietButton>
                        <Tooltip
                          title={selfActiveGuard ? 'You cannot deactivate your own account.' : ''}
                          disableHoverListener={!selfActiveGuard}
                        >
                          <span>
                            <QuietButton
                              size="small"
                              disabled={selfActiveGuard}
                              onClick={() => setStatusTarget(u)}
                              sx={u.is_active ? { color: 'error.main', borderColor: 'error.main' } : undefined}
                            >
                              {u.is_active ? 'Deactivate' : 'Reactivate'}
                            </QuietButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={isSelf ? 'You cannot delete your own account.' : ''}
                          disableHoverListener={!isSelf}
                        >
                          <span>
                            <QuietButton
                              size="small"
                              disabled={isSelf}
                              onClick={() => setDeleteTarget(u)}
                              sx={{ color: 'error.main', borderColor: 'error.main' }}
                            >
                              Delete
                            </QuietButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </Paper>
      )}

      <UserFormDialog
        open={dialogOpen}
        user={editTarget}
        onClose={() => setDialogOpen(false)}
        onSaved={() => void reload()}
      />

      <ConfirmDialog
        open={statusTarget !== null}
        title={statusTarget?.is_active ? 'Deactivate user' : 'Reactivate user'}
        body={statusTarget ? `${statusAction} ${statusTarget.email}?` : ''}
        confirmLabel={statusAction}
        confirmColor={statusTarget?.is_active ? 'error' : 'primary'}
        onConfirm={async () => {
          if (statusTarget) await api.users.update(statusTarget.id, { is_active: !statusTarget.is_active });
        }}
        onClose={() => setStatusTarget(null)}
        onSuccess={() => void reload()}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete user"
        body={
          deleteTarget ? (
            <>
              <Typography>Delete {deleteTarget.email}? This cannot be undone.</Typography>
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Deactivating keeps the record -- use it for staff who have left. Deleting is for mistakes or test
                accounts only.
              </Typography>
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={async () => {
          if (deleteTarget) await api.users.remove(deleteTarget.id);
        }}
        onClose={() => setDeleteTarget(null)}
        onSuccess={() => void reload()}
      />
    </Box>
  );
}

export default UsersPage;
